import { z } from 'zod';
import { PanelRoleSchema } from './me';

// GET /app_service/panel/v1/access/assignments as app-service answers it ([AS-077], CBX-11681).
// Unknown keys are stripped, so only what the screen renders ever reaches the cache: never a name
// or a phone.

// A role as the catalog lists it. `assignable: false` marks a role the screen shows but never
// grants — «Sistemas» is listed so the screen can compute effective permissions and recognise the
// rows it must leave alone.
export const CatalogRoleSchema = z.object({
  id: z.string().min(1),
  // Blank is tolerated, as in /me: the screen shows the id instead.
  name: z.string(),
  permissions: z.array(z.string()),
  assignable: z.boolean(),
});

// Backend decides which permissions are restricted. A missing flag reads as restricted (fail
// closed). The panel mirrors the delivered contract but does not use the flag today.
export const CatalogPermissionSchema = z
  .object({
    id: z.string().min(1),
    restricted: z.boolean().optional(),
  })
  .transform(({ id, restricted }) => ({ id, restricted: restricted !== false }));

// One person's Asignación. Roles have the same `{id, name}` shape as `/me`.
export const RoleAssignmentSchema = z
  .object({
    email: z.string().min(1),
    roles: z.array(PanelRoleSchema),
    // Plain strings: an extra the panel does not know must parse; the screen shows it read-only.
    extra_permissions: z.array(z.string()),
    // Opaque: the panel only sends it back with a write, never compares or orders it.
    version: z.string().min(1),
  })
  .transform(({ email, roles, extra_permissions, version }) => ({
    email,
    roles,
    extraPermissions: extra_permissions,
    version,
  }));

export const RoleAssignmentsResponseSchema = z.object({
  roles: z.array(CatalogRoleSchema),
  permissions: z.array(CatalogPermissionSchema),
  assignments: z.array(RoleAssignmentSchema),
});

export type CatalogRole = z.output<typeof CatalogRoleSchema>;
export type CatalogPermission = z.output<typeof CatalogPermissionSchema>;
export type RoleAssignment = z.output<typeof RoleAssignmentSchema>;
export type RoleAssignmentsResponse = z.output<typeof RoleAssignmentsResponseSchema>;

// The whole of one person's Asignación, in the panel's own terms; the endpoint maps it to the wire
// keys. `extraPermissions` is the complete set: an extra left out is removed. `expectedVersion:
// null` claims «this person has no row yet»; it is a claim the server checks, not a skip.
export interface RoleAssignmentWrite {
  email: string;
  expectedVersion: string | null;
  roles: string[];
  extraPermissions: string[];
}
